import { useState, useEffect, useRef, useCallback, useContext } from 'react'
import { EventGQL, fetchSingleEventInfo } from '../../Api'
import { checkLiveGameRunning, isLiveGame } from '../../Utils'
import {
  GAME_CHECK_INTERVAL_TIME,
  GAME_POLLING_INTERVAL_TIME,
} from '../../Constants/season'
import { GameDataHookReturn } from './types'
import { LeagueContext } from '../../Context'

export const useLiveGameData = (): GameDataHookReturn => {
  const { events, eventsLoading, updateEventInState } = useContext(LeagueContext)!
  const [liveGameEvent, setLiveGameEvent] = useState<EventGQL | null>(null)
  const [isEventLoading, setIsEventLoading] = useState<boolean>(false)

  const gameCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const liveGameIntervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (eventsLoading) return
    runGameCheckInterval()
  }, [eventsLoading])

  // --- State Updaters ---

  const updateLiveEventData = useCallback((eventInfo: EventGQL) => {
    if (!eventInfo) {
      setLiveGameEvent(null)
      setIsEventLoading(false)
      return
    }

    setLiveGameEvent(eventInfo)
    updateEventInState(eventInfo)
    setIsEventLoading(false)
  }, [])

  // --- Fetching Logic ---

  const fetchEvent = useCallback(async (gameId: string): Promise<EventGQL | null> => {
    try {
      const eventInfo = await fetchSingleEventInfo(gameId)
      if (!eventInfo) return null
      updateLiveEventData(eventInfo)
      return eventInfo
    } catch (err) {
      console.error('Error fetching event data:', err)
      setIsEventLoading(false)
      return null
    }
  }, [])

  const runGameCheckInterval = useCallback((): void => {
    if (gameCheckIntervalRef.current !== null) return

    // console.log('Starting game check interval')
    gameCheckIntervalRef.current = setInterval(() => {
      try {
        if (!events?.length) return
        const liveEv = checkLiveGameRunning(events)
        // console.log(`REST check: Game is ${liveEv ? 'live' : 'not live'}.`)
        if (liveEv) {
          startLiveGameInterval(liveEv)
        } else if (liveGameIntervalRef.current) {
          console.log('Game ended, clearing polling interval')
          clearInterval(liveGameIntervalRef.current)
          liveGameIntervalRef.current = null
          setLiveGameEvent(null)
        }
      } catch (err) {
        console.error('Error in game check interval:', err)
      }
    }, GAME_CHECK_INTERVAL_TIME)
  }, [])

  const startLiveGameInterval = useCallback(async (liveEv: EventGQL) => {
    if (liveGameIntervalRef.current !== null || !liveEv?.slugname) return

    console.log('Starting REST polling interval')
    liveGameIntervalRef.current = setInterval(async () => {
      console.log('Polling for live game data...')
      try {
        const eventData = await fetchEvent(liveEv.slugname)
        if (eventData) {
          if (!isLiveGame(eventData)) {
            console.log('Game ended, stopping REST polling')
            updateEventInState(eventData)
            setLiveGameEvent(null)
            if (liveGameIntervalRef.current !== null) {
              clearInterval(liveGameIntervalRef.current)
              liveGameIntervalRef.current = null
            }
          }
        }
      } catch (error) {
        console.error('Error in live game interval execution:', error)
      }
    }, GAME_POLLING_INTERVAL_TIME)
  }, [])

  return {
    liveGameEvent,
    isEventLoading,
  }
}
